import {CampaignType} from 'common/src/modules/campaigns/campaigns.types';
import {contentFeedsApi} from 'common/src/modules/contentFeeds/contentFeeds.api';
import {contentSetsApi} from 'common/src/modules/contentSets/contentSets.api';
import {creativesApi} from 'common/src/modules/creatives/creatives.api';
import {TemplateType} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import locationFeedApi from 'common/src/modules/locationFeed/locationFeed.api';
import locationSetsApi from 'common/src/modules/locationSets/locationSets.api';
import productFeedApi from 'common/src/modules/productFeed/productFeed.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import {SearchObjectType} from './searchObjectsTool.enums';

const searchObjectsToolSchema = z.object({
  type: z.nativeEnum(SearchObjectType),
  searchPrompt: z
    .string()
    .describe('Short, consise, whatever you know from user prompt, dont invent extra details'),
});

export default function searchObjectsTool(store: CLStore) {
  async function execute({
    input: {type, searchPrompt},
  }: ToolExecuteParams<typeof searchObjectsToolSchema>): Promise<ToolResult> {
    try {
      const searchFunctions = {
        [SearchObjectType.Creative]: (prompt: string) => _searchCreatives(store, prompt),
        [SearchObjectType.ContentFeed]: _searchContentFeeds,
        [SearchObjectType.ContentSet]: _searchContentSets,
        [SearchObjectType.ProductFeed]: _searchProductFeeds,
        [SearchObjectType.LocationFeed]: _searchLocationFeeds,
        [SearchObjectType.LocationSet]: _searchLocationSets,
      };

      const searchFunction = searchFunctions[type];

      if (!searchFunction) {
        return toolResult(`Unsupported object type: ${type}`);
      }

      const resultArray = await searchFunction(searchPrompt);

      return resultArray.length === 0
        ? toolResult('No relevant objects found')
        : toolResult(JSON.stringify(resultArray));
    } catch (error) {
      return toolResult(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.SearchObjects,
    displayName: 'Searching libraries',
    description:
      'Search for account libraries for relevant objects. When searching by exact IDs of the selected type, provide all IDs in one call. Otherwise, make separate calls per object.',
    execute,
    inputSchema: searchObjectsToolSchema,
  });
}

async function _searchCreatives(store: CLStore, prompt: string) {
  const campaign = store.getState().campaignPage.campaignTree.campaign;

  if (!campaign) {
    throw new Error('No campaign found.');
  }

  // Get Template Type for the campaign
  const templateTypes =
    campaign.type === CampaignType.VideoAds
      ? [TemplateType.VideoAds]
      : [
          TemplateType.InteractiveVideoAds,
          TemplateType.ExternalAds,
          TemplateType.InBannerVideoAds,
          TemplateType.DisplayAds,
          TemplateType.ImageAds,
        ];

  const creativesResponse = await creativesApi.searchCreatives({prompt, templateTypes});

  return creativesResponse.creatives.map(({id, name}) => ({id, name}));
}

async function _searchContentFeeds(prompt: string) {
  const searchResponse = await contentFeedsApi.searchFeeds(prompt);

  return searchResponse.contentFeeds.map(({id, name}) => ({id, name}));
}

async function _searchContentSets(prompt: string) {
  const searchResponse = await contentSetsApi.searchContentSets(prompt);

  return searchResponse.contentSets.map(({id, name, feedId}) => ({id, name, feedId}));
}

async function _searchProductFeeds(prompt: string) {
  const searchResponse = await productFeedApi.searchFeeds(prompt);

  return searchResponse.productFeeds.map(({id, name, productType}) => ({id, name, productType}));
}

async function _searchLocationFeeds(prompt: string) {
  const searchResponse = await locationFeedApi.searchFeeds(prompt);

  return searchResponse.locationFeeds.map(({id, name}) => ({id, name}));
}

async function _searchLocationSets(prompt: string) {
  const searchResponse = await locationSetsApi.searchLocationSets(prompt);

  return searchResponse.locationSets.map(({id, name, feedId}) => ({id, name, feedId}));
}
